import {
  useCallback, useMemo, useEffect, useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// Helper function to serialize complex values to URL-safe strings
function serializeValue(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  // Handle dayjs objects
  if (dayjs.isDayjs(value)) {
    return `date/${value.format('YYYY-MM-DD')}`;
  }

  // Handle Date objects
  if (value instanceof Date) {
    return `date/${dayjs(value).format('YYYY-MM-DD')}`;
  }

  // Handle {value, label} objects (from labelInValue components)
  if (typeof value === 'object' && value.value !== undefined && value.label !== undefined) {
    return `vl/${value.value}-${encodeURIComponent(value.label)}`;
  }

  // Handle date ranges (array of 2 dayjs objects)
  if (Array.isArray(value) && value.length === 2 && value.every((v) => dayjs.isDayjs(v))) {
    return `daterange/${value[0].format('YYYY-MM-DD')}-${value[1].format('YYYY-MM-DD')}`;
  }

  // Handle other objects as JSON (fallback)
  if (typeof value === 'object') {
    try {
      return `json/${encodeURIComponent(JSON.stringify(value))}`;
    } catch (e) {
      console.warn('Failed to serialize object for URL:', value);
      return null;
    }
  }

  return String(value);
}

// Helper function to deserialize values from URL strings
function deserializeValue(value) {
  if (!value || value === '') {
    return value;
  }

  // Handle date format: date/YYYY-MM-DD
  if (value.startsWith('date/')) {
    const dateStr = value.slice(5); // Remove 'date/' prefix
    return dayjs(dateStr);
  }

  // Handle {value, label} format: vl/value-label
  if (value.startsWith('vl/')) {
    const valueLabel = value.slice(3); // Remove 'vl/' prefix
    const dashIndex = valueLabel.indexOf('-');
    if (dashIndex > 0) {
      return {
        value: valueLabel.slice(0, dashIndex),
        label: decodeURIComponent(valueLabel.slice(dashIndex + 1)),
      };
    }
  }

  // Handle date range format: daterange/YYYY-MM-DD-YYYY-MM-DD
  if (value.startsWith('daterange/')) {
    const rangeStr = value.slice(10); // Remove 'daterange/' prefix
    const [startDate, endDate] = rangeStr.split('-');
    if (startDate && endDate) {
      return [dayjs(startDate), dayjs(endDate)];
    }
  }

  // Handle JSON format: json/encoded-json
  if (value.startsWith('json/')) {
    const jsonStr = decodeURIComponent(value.slice(5)); // Remove 'json/' prefix
    try {
      return JSON.parse(jsonStr);
    } catch (e) {
      console.warn('Failed to parse JSON from URL:', jsonStr);
      return value;
    }
  }

  return value;
}

export function useQueryParams(initialParams = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const hasInitialized = useRef(false);

  // Use current path if baseUrl is not provided
  const resolvedBaseUrl = location.pathname;

  // Parse current query parameters
  const parameters = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = {};

    // Convert URLSearchParams to plain object and deserialize values
    Array.from(searchParams.entries()).forEach(([key, value]) => {
      params[key] = deserializeValue(value);
    });

    return params;
  }, [location.search]);


  // Function to update parameters and navigate
  const setParameters = useCallback((newParams, options = {}) => {
    if (typeof newParams === 'function') {
      newParams = newParams(parameters);
    }
    const { merge = true, replace = true } = options;
    const searchParams = new URLSearchParams();

    // If merge is true, start with current parameters
    if (merge) {
      Object.entries(parameters).forEach(([key, value]) => {
        const serialized = serializeValue(value);
        if (serialized !== null) {
          searchParams.set(key, serialized);
        }
      });
    }

    // Add/update new parameters
    Object.entries(newParams).forEach(([key, value]) => {
      const serialized = serializeValue(value);
      if (serialized !== null) {
        searchParams.set(key, serialized);
      } else if (merge) {
        // Remove parameter if value is empty and we're merging
        searchParams.delete(key);
      }
    });

    // Generate the new URL with query parameters
    const queryString = searchParams.toString();
    const newUrl = queryString ? `${resolvedBaseUrl}?${queryString}` : resolvedBaseUrl;

    // Navigate to the new URL
    navigate(newUrl, { replace });
  }, [resolvedBaseUrl, navigate, parameters]);

  // Set initial parameters on first render if they're not already in URL
  useEffect(() => {
    if (!hasInitialized.current && Object.keys(initialParams).length > 0) {
      hasInitialized.current = true;

      // Check if any initial params are missing from current URL
      const missingParams = {};
      Object.entries(initialParams).forEach(([key, value]) => {
        if (!(key in parameters) && serializeValue(value) !== null) {
          missingParams[key] = value;
        }
      });

      // Set missing initial parameters
      if (Object.keys(missingParams).length > 0) {
        setParameters(missingParams, { merge: true });
      }
    }
  }, [initialParams, parameters, setParameters]);

  return useMemo(() => ([
    parameters,
    setParameters,
  ]), [parameters, setParameters]);
}
